import { canvasColors } from "./workspace-agent-contract";
/*
 * Tool errors in plain words. The same text goes back to the model and into
 * the agent panel, so it names the operation and field that failed and what
 * would be accepted, instead of a raw validation dump.
 */

type Issue = {
  code?: string;
  path?: (string | number)[];
  message?: string;
  values?: unknown[];
  minimum?: number | bigint;
  maximum?: number | bigint;
  origin?: string;
  expected?: string;
  keys?: string[];
};

function where(path: (string | number)[] = []) {
  const parts = [...path];
  const lead =
    typeof parts[0] === "number"
      ? `Operation ${(parts.shift() as number) + 1}`
      : "";
  const field = parts.join(".");
  return [lead, field && `"${field}"`].filter(Boolean).join(" ") || "The edit";
}

function describe(issue: Issue) {
  const subject = where(issue.path);
  if (issue.code === "invalid_format" && issue.path?.at(-1) === "color")
    return `${subject} must be one of ${canvasColors.options.join(", ")}, or a six-digit hex colour (#RRGGBB)`;
  if (issue.code === "invalid_value" && issue.values?.length)
    return `${subject} must be one of ${issue.values.map(String).join(", ")}`;
  if (issue.code === "too_small")
    return issue.origin === "string" && Number(issue.minimum) <= 1
      ? `${subject} must not be empty`
      : `${subject} must be at least ${issue.minimum}`;
  if (issue.code === "too_big")
    return `${subject} must be at most ${issue.maximum}`;
  if (issue.code === "unrecognized_keys" && issue.keys?.length)
    return `${subject} has unsupported field${issue.keys.length === 1 ? "" : "s"} ${issue.keys.join(", ")}`;
  if (issue.code === "invalid_type")
    return /received undefined/.test(issue.message ?? "")
      ? `${subject} is missing`
      : `${subject} must be a ${issue.expected ?? "different type"}`;
  if (issue.code === "invalid_union")
    return `${subject} is not a supported operation`;
  return `${subject}: ${issue.message ?? "is not valid"}`;
}

function issuesOf(error: unknown): Issue[] | undefined {
  if (
    error &&
    typeof error === "object" &&
    Array.isArray((error as { issues?: unknown }).issues)
  )
    return (error as { issues: Issue[] }).issues;
  const message =
    error instanceof Error
      ? error.message
      : typeof error === "string"
        ? error
        : "";
  // Older conversations stored the raw validation array as the message.
  if (message.trim().startsWith("[")) {
    try {
      const parsed = JSON.parse(message);
      if (
        Array.isArray(parsed) &&
        parsed.every((i) => i && typeof i === "object" && "message" in i)
      )
        return parsed as Issue[];
    } catch {}
  }
  return undefined;
}

export function plainToolError(error: unknown): string {
  const issues = issuesOf(error);
  if (issues?.length) {
    const listed = issues.slice(0, 3).map(describe);
    const more = issues.length > 3 ? `, and ${issues.length - 3} more` : "";
    return `The edit was not valid: ${listed.join("; ")}${more}.`;
  }
  if (error instanceof SyntaxError)
    return `operations_json is not valid JSON (${error.message}). Use double-quoted property names and strings, with no trailing commas.`;
  if (error instanceof Error && error.message) return error.message;
  if (typeof error === "string" && error) return error;
  return "The tool failed.";
}
